import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { uploadVideoToCloudinary } from '../../config/cloudinary';
import '../../styles/admin.css';

const ManageGallery = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const imagesRef = collection(db, 'gallery');
      const q = query(imagesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const imagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setImages(imagesData);
    } catch (error) {
      console.error('Error fetching images:', error);
      alert('Error loading images');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
      } else {
        alert('Please select an image file');
        e.target.value = '';
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select an image file first');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const result = await uploadVideoToCloudinary(selectedFile, (progress) => {
        setUploadProgress(progress);
      });

      setFormData({ ...formData, url: result.url });
      alert('Image uploaded successfully!');
      setSelectedFile(null);
      setUploadProgress(0);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.url) {
      alert('Please upload an image or provide an image URL');
      return;
    }

    setLoading(true);

    try {
      if (editingImage) {
        await updateDoc(doc(db, 'gallery', editingImage.id), {
          title: formData.title,
          description: formData.description,
          url: formData.url,
          updatedAt: serverTimestamp()
        });
        alert('Image updated successfully!');
      } else {
        await addDoc(collection(db, 'gallery'), {
          title: formData.title,
          description: formData.description,
          url: formData.url,
          createdAt: serverTimestamp()
        });
        alert('Image added successfully!');
      }

      setFormData({ title: '', description: '', url: '' });
      setEditingImage(null);
      fetchImages();
    } catch (error) {
      console.error('Error saving image:', error);
      alert('Failed to save image');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      title: image.title || '',
      description: image.description || '',
      url: image.url
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (imageId) => {
    if (!confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'gallery', imageId));
      alert('Image deleted successfully!');
      fetchImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image');
    }
  };

  const handleCancel = () => {
    setFormData({ title: '', description: '', url: '' });
    setEditingImage(null);
    setSelectedFile(null);
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <button onClick={() => navigate('/admin')} className="back-btn">
          ← Back to Dashboard
        </button>
        <h1 className="admin-title">Manage Gallery</h1>
      </div>

      <div className="admin-container">
        <div className="admin-card">
          <h2 className="card-title">
            {editingImage ? 'Edit Image' : 'Add New Image'}
          </h2>

          <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-group">
              <label htmlFor="title">Image Title</label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter image title (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter image description (optional)"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="file">Upload Image to Cloudinary</label>
              <input
                type="file"
                id="file"
                accept="image/*"
                onChange={handleFileChange}
              />
              {selectedFile && (
                <div className="file-info">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </div>
              )}
              {selectedFile && !uploading && (
                <button
                  type="button"
                  onClick={handleUpload}
                  className="btn btn-secondary"
                  style={{ marginTop: '0.5rem' }}
                >
                  Upload to Cloudinary
                </button>
              )}
              {uploading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                  <p>Uploading: {uploadProgress}%</p>
                </div>
              )}
            </div>

            {formData.url && (
              <div className="video-preview">
                <label>Preview</label>
                <img
                  src={formData.url}
                  alt="Preview"
                  style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px' }}
                />
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading || uploading}>
                {loading ? 'Saving...' : editingImage ? 'Update Image' : 'Add Image'}
              </button>
              {editingImage && (
                <button type="button" onClick={handleCancel} className="btn btn-secondary">
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="admin-card">
          <h2 className="card-title">All Images ({images.length})</h2>

          {loading && <div className="loading-text">Loading images...</div>}

          {!loading && images.length === 0 && (
            <div className="empty-state">
              <p>No images added yet. Add your first image above!</p>
            </div>
          )}

          {!loading && images.length > 0 && (
            <div className="videos-grid">
              {images.map((image) => (
                <div key={image.id} className="video-item">
                  <div className="video-thumbnail">
                    <img
                      src={image.url}
                      alt={image.title || 'Gallery image'}
                      style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '8px' }}
                    />
                  </div>
                  <div className="video-details">
                    <h3>{image.title || 'Untitled'}</h3>
                    {image.description && <p>{image.description}</p>}
                  </div>
                  <div className="video-actions">
                    <button onClick={() => handleEdit(image)} className="btn-edit">
                      Edit
                    </button>
                    <button onClick={() => handleDelete(image.id)} className="btn-delete">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageGallery;
